import React from 'react';
import { Box, Skeleton, Paper, Typography } from '@mui/material';

// Chart skeleton with animated placeholders
export const ChartSkeleton = ({ height = 400, title = true, description = true }) => (
  <Paper sx={{ p: 3, height: "100%" }}>
    {title && (
      <Skeleton 
        variant="text" 
        width="60%" 
        height={32} 
        sx={{ mb: 1 }}
        animation="wave"
      />
    )}
    {description && (
      <Skeleton 
        variant="text" 
        width="80%" 
        height={20} 
        sx={{ mb: 3 }}
        animation="wave"
      />
    )}
    <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Skeleton 
        variant="rounded" 
        width="100%" 
        height="90%" 
        animation="wave"
        sx={{ borderRadius: 2 }}
      />
    </Box>
  </Paper>
);

// Time series specific skeleton
export const TimeSeriesSkeleton = () => (
  <ChartSkeleton height={400}>
    <Box sx={{ position: 'absolute', bottom: 60, left: 0, right: 0, px: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" width={80} height={20} />
        ))}
      </Box>
    </Box>
  </ChartSkeleton>
);

// Pie chart specific skeleton
export const PieChartSkeleton = () => (
  <Paper sx={{ p: 3, height: "100%", minHeight: 700 }}>
    <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
    <Skeleton variant="text" width="80%" height={20} sx={{ mb: 3 }} />
    <Box sx={{ display: 'flex', alignItems: 'center', height: 600 }}>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <Skeleton variant="circular" width={300} height={300} />
      </Box>
      <Box sx={{ width: 200, ml: 4 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="rectangular" width={16} height={16} sx={{ mr: 1 }} />
            <Skeleton variant="text" width="70%" height={20} />
          </Box>
        ))}
      </Box>
    </Box>
  </Paper>
);

// Map skeleton
export const MapSkeleton = () => (
  <ChartSkeleton height={500}>
    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
      <Skeleton variant="rounded" width={400} height={250} sx={{ borderRadius: 3 }} />
    </Box>
  </ChartSkeleton>
);

// Table skeleton
export const TableSkeleton = ({ rows = 10, columns = 5 }) => (
  <Paper sx={{ p: 2 }}>
    {/* Header */}
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} variant="text" width="100%" height={40} />
      ))}
    </Box>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <Box key={rowIndex} sx={{ display: 'flex', gap: 2, mb: 1 }}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton key={colIndex} variant="text" width="100%" height={32} />
        ))}
      </Box>
    ))}
  </Paper>
);

// Grid skeleton for dashboard cards
export const DashboardSkeleton = () => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
    {Array.from({ length: 6 }).map((_, i) => (
      <Paper key={i} sx={{ p: 3, height: 400 }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="90%" height={20} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" width="100%" height="70%" />
      </Paper>
    ))}
  </Box>
);
